import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { mapCustomerAgingApiToUi } from "@/lib/mappers/customer-detail";
import { mockInvoicesByCustomerId } from "@/lib/demo-data/customer-transactions";
import { isStubResponse } from "@/types/api/customer-detail";
import type { CustomerAgingApi, StubResponse } from "@/types/api/customer-detail";
import type { CustomerAgingUI, InvoiceUI } from "@/types/ui/customer-detail";

function calculateDemoAgeing(invoices: InvoiceUI[]): CustomerAgingUI {
  const today = new Date();

  const aging = {
    current: 0,
    days1_30: 0,
    days31_60: 0,
    days61_90: 0,
    days90Plus: 0,
  };

  for (const invoice of invoices) {
    const dueDate = new Date(invoice.dueDate);
    const daysOverdue = Math.floor(
      (today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysOverdue <= 30) {
      aging.current += invoice.amount;
    } else if (daysOverdue <= 60) {
      aging.days1_30 += invoice.amount;
    } else if (daysOverdue <= 90) {
      aging.days31_60 += invoice.amount;
    } else if (daysOverdue <= 120) {
      aging.days61_90 += invoice.amount;
    } else {
      aging.days90Plus += invoice.amount;
    }
  }

  const total = aging.current + aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;
  const overdue = aging.days1_30 + aging.days31_60 + aging.days61_90 + aging.days90Plus;

  return { ...aging, total, overdue };
}

export function useCustomerAgeing(customerId: string | undefined) {
  const { apiGet } = useApi();

  const demoInvoices = customerId ? mockInvoicesByCustomerId[customerId] ?? [] : [];
  const demoAgeing = calculateDemoAgeing(demoInvoices);

  return useModeAwareQuery<CustomerAgingUI | null, Error>(
    ["customer-ageing", customerId],
    async ({ signal }) => {
      if (!customerId) return null;

      try {
        const response = await apiGet<CustomerAgingApi | StubResponse>(
          `/api/v1/customers/${customerId}/aging`,
          undefined,
          signal
        );

        if (isStubResponse(response)) {
          return null;
        }

        return mapCustomerAgingApiToUi(response);
      } catch (error) {
        console.error("Error fetching customer ageing:", error);
        return null;
      }
    },
    customerId ? demoAgeing : null,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}
